import { Box, Button } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement, useState } from "react";
import { Link, generatePath, useSearchParams } from "react-router-dom";

// Components
import LinkableTableCell from "../../../shared/DataTable/shared/LinkableTableCell";
import LayoutWrapper from "../../../shared/layout";
import AttorneyClaims from "./index/AttorneyClaims";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";
import { getCacheKeyObject } from "../../../shared/DataTable/hooks/useSearchParams";

// Utils
import { RouterPathMapping } from "../../../Router";
import { getClaimsList } from "../../../utils/api/claims";
import { ClaimStatus, dummyPaginatedApiResponse } from "../../../utils/consts";
import {
	convertProtoDateToDateTime,
	convertUnixTimestampToLocalDate,
	getAttorneyClaimStatusFilterOptions,
	getClaimStatusFromBffStatus,
	processClaimStatusForAttorney
} from "../../../utils/helpers";
import {
	CustomColumnFilterType,
	DataTableColumn,
	KVP,
	PaginatedApiResponse,
	PaginationResponse
} from "../../../utils/types";

// Protos
import {
	Claim as ApiClaim,
	ClaimPerson,
	ClaimsListResponse
} from "../../../utils/api/protos/messages/claims.message_pb";
import { PaginationResponse as ApiPaginationResponse } from "../../../utils/api/protos/messages/core.message_pb";

interface AttorneyClaimsPageProps {}

/**
 * Define columns to show in the DataTable
 */
const columns: Array<DataTableColumn> = [
	{
		header: "Claim Id",
		id: "id",
		filterable: true,
		minWidth: "5ch",
		isHidden: false,
		isSelectable: true,
		linkable: true,
		isSortable: true,
		allData: true,
		valueMap: (claim: KVP) => (
			<LinkableTableCell
				To={generatePath(RouterPathMapping.Attorney_ClaimDetail, { id: claim["id"]?.toString() })}
				Contents={claim["id"]}
			/>
		)
	},
	{
		header: "PASS Case ID",
		id: "caseId",
		filterable: true,
		minWidth: "5ch",
		isHidden: false,
		isSortable: true,
		allData: true,
		valueMap: (claim: KVP) => (
			<LinkableTableCell
				To={generatePath(RouterPathMapping.Attorney_CaseDetail, { id: claim["caseId"]?.toString() })}
				Contents={claim["caseId"]}
			/>
		)
	},
	{
		header: "LAMP Case ID",
		id: "lampCaseId",
		filterable: true,
		minWidth: "8ch",
		isHidden: false,
		isSortable: true,
		allData: false
	},
	{
		header: "Attorney ID",
		id: "attyId",
		filterable: true,
		minWidth: "5ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Member",
		id: "memberName",
		filterable: true,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Amount",
		id: "totalNetworkCost",
		filterable: false,
		minWidth: "5ch",
		isHidden: false,
		isSortable: true,
		valueMap: (totalNetworkCost: string) =>
			parseFloat(totalNetworkCost).toLocaleString(undefined, { style: "currency", currency: "USD" })
	},
	{
		header: "Status",
		id: "status",
		filterable: true,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true,
		customFilter: {
			type: CustomColumnFilterType.Select,
			options: getAttorneyClaimStatusFilterOptions()
		}
	},
	{
		header: "Submitted",
		id: "submittedAt",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true,
		isDate: true
	},
	{
		header: "Due",
		id: "dueDate",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true,
		isDate: true
	},
	{
		header: "",
		id: "actions",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSelectable: false,
		linkable: false,
		isSortable: false,
		allData: true,
		valueMap: (claim: KVP) =>
			claim["status"] === ClaimStatus.Draft ? (
				<Button
					as={Link}
					to={generatePath(RouterPathMapping.Attorney_ClaimIntake, { id: claim["id"]?.toString() })}
					marginRight={4}
					size="sm"
					minWidth="74px"
					colorScheme="orange"
					padding={2}>
					Continue
				</Button>
			) : (
				<Button
					as={Link}
					to={generatePath(RouterPathMapping.Attorney_ClaimDetail, { id: claim["id"]?.toString() })}
					marginRight={4}
					size="sm"
					minWidth="74px"
					colorScheme="orange"
					padding={2}>
					View
				</Button>
			)
	}
];

/**
 * Handles the Claims listing page for a single attorney
 */
const AttorneyClaimsPage: FunctionComponent<AttorneyClaimsPageProps> = (
	props: AttorneyClaimsPageProps
): ReactElement => {
	// State
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [claimsData, setClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [localCacheKey, setLocalCacheKey] = useState<string>("");

	// Hooks
	const { getUser } = useAuth();
	const [searchParams] = useSearchParams();
	const user = getUser();
	const toast = useClaimsToast();

	/**
	 * Helper function to allow for child objects to trigger a fetch of Claims data
	 * with the active tab's cacheKey
	 * @param cacheKey The cacheKey of the currently active table
	 */
	const handleFetchClaimsData = (cacheKey: string): void => {
		if (!isEmpty(cacheKey)) {
			setIsLoading(true);
			if (cacheKey !== localCacheKey) setLocalCacheKey(cacheKey);
			getClaimsList(getCacheKeyObject(cacheKey), undefined, parseInt(user?.externalId ?? "0"))
				.then((response: ClaimsListResponse) => {
					// Build response data
					const returnedClaimsData: Array<{ [key: string]: string | number }> = response
						.getClaimList()
						.map((claim: ApiClaim) => {
							const atty: ClaimPerson | undefined = claim.getAtty();
							const member: ClaimPerson | undefined = claim.getMember();
							if (atty) {
								const newClaim: { [key: string]: string | number } = {
									id: claim.getId(),
									caseId: claim.getCaseId(),
									lampCaseId: claim.getLampCaseId(),
									atty: atty.getName(),
									attyId: atty.getId(),
									memberName: member?.getName() ?? "",
									status: processClaimStatusForAttorney(getClaimStatusFromBffStatus(claim.getStatus())),
									totalNetworkCost: claim.getTotalNetworkCost()?.getValue() ?? "",
									submittedAt: claim.getSubmittedAt()
										? convertUnixTimestampToLocalDate(claim.getSubmittedAt()).toLocaleString()
										: "",
									dueDate: claim.getDueDate() ? convertProtoDateToDateTime(claim.getDueDate()).toLocaleString() : ""
								};

								return newClaim;
							} else {
								console.error(
									"Encountered a problem wherein a call to fetch the Claim was emitted but the Atty/Member was not found!"
								);
								toast("Error", "There was a problem fetching the Claim", "error");
								throw Error("attempted to process a claim with missing atty/member");
							}
						});
					const foundPaginationData: ApiPaginationResponse | undefined = response.getPaginationResponse();
					if (foundPaginationData) {
						const returnedPaginationData: PaginationResponse = {
							current_page: foundPaginationData.getCurrentPage(),
							first_page: foundPaginationData.getFirstPage(),
							last_page: foundPaginationData.getLastPage(),
							perPage: foundPaginationData.getLimit(),
							total: foundPaginationData.getTotal()
						};

						setClaimsData({
							data: returnedClaimsData,
							meta: returnedPaginationData
						});
					} else {
						toast("Error", "There was a problem deciphering the pagination metadata", "error");
						console.error("Attempting to parse pagination metadata that does not exist!!");
					}
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	return (
		<>
			<LayoutWrapper>
				<Box m="10">
					<AttorneyClaims
						claimsData={claimsData}
						columns={columns}
						searchParams={searchParams}
						handleFetchClaims={handleFetchClaimsData}
						isLoading={isLoading}
					/>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default AttorneyClaimsPage;
